import { debounce } from "lodash";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";

import { faFilterList } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Direction, Flex, Justify } from "@ntemarked/mittne-components/dist/Flex/Flex";

import Error from "components/molecules/Error";
import Loading from "components/molecules/Loading";
import { MainLayoutPageHeader } from "components/organisms/MainLayout";
import ModalButton from "components/organisms/ModalButton";
import Pagination from "components/organisms/Pagination";
import UserRow from "components/organisms/UserRow";
import FormButton from "components/organisms/form/FormButton";
import useParamsState from "hooks/useParamsState";
import useQueryParamsState from "hooks/useQueryParamsState";
import UsersFilter from "pages/Strombestilling/orders/components/UsersFilter";
import { useGridOwnersQuery, usePaginateUsersQuery } from "pages/Users/Users.graphql-gen";

import { OwnerType, User } from "generated/graphql-types";

const PER_PAGE = 40;

export default function UsersPage() {
	const [search, setSearch] = useQueryParamsState("search");
	const [currentPage, setCurrentPage] = useQueryParamsState("page");
	const [gridOwner, setGridOwner] = useQueryParamsState("gridOwner");
	const [ownerType, setOwnerType] = useParamsState<OwnerType[]>("ownerType", []);
	const [notCustomer, setNotCustomer] = useParamsState("notCustomer", false);
	const [production, setProduction] = useParamsState<boolean | null>("production", null);
	const [fjutt, setFjutt] = useParamsState<boolean | null>("fjutt", null);

	const [clientSearch, setClientSearch] = useState(search ?? "");
	const [clientPage, setClientPage] = useState(Number(currentPage) || 0);

	const debouncedSetSearch = useMemo(() => debounce((newSearch: string) => setSearch(newSearch), 200), [setSearch]);

	function resetFilters() {
		setClientSearch("");
		setSearch("");
		setGridOwner("");
		setOwnerType([]);
		setNotCustomer(false);
	}

	// If either gridOwner or notCustomer is not null, the filter is considered active.
	const anyFilterActivated = Boolean((gridOwner ?? notCustomer) || ownerType.length > 0 || production !== null || fjutt !== null);

	const [showFilters, setShowFilters] = useState(false);

	const { data: countData } = usePaginateUsersQuery({
		variables: {
			startIndex: 0,
			count: 1,
		},
	});

	const { data: gridOwners } = useGridOwnersQuery();

	const { data, previousData, loading, error } = usePaginateUsersQuery({
		variables: {
			search: (!!search && search.replace(/"/g, "")) || undefined,
			startIndex: currentPage ? PER_PAGE * (Number(currentPage) - 1) : 0,
			count: PER_PAGE,
			UserFilter: {
				gridOwner: gridOwner || undefined,
				ownerType: ownerType ?? undefined,
				production: production ?? undefined,
				fjutt: fjutt ?? undefined,
				notCustomer,
			},
		},
	});

	const totalPages = (data?.paginatedUsers?.total ?? previousData?.paginatedUsers?.total ?? 1) as number;

	let countText = undefined;

	if (countData?.paginatedUsers?.total) {
		if (countData?.paginatedUsers?.total === data?.paginatedUsers?.total) {
			countText = data?.paginatedUsers?.total + " brukere";
		} else {
			countText = `${data?.paginatedUsers?.total ?? "..."} av ${countData?.paginatedUsers?.total ?? "..."} brukere`;
		}
	}

	return (
		<Flex direction={Direction.column} gap="calc( var(--grid-2) * 2)">
			<Flex direction={Direction.row} justifyContent={Justify.spaceBetween}>
				<MainLayoutPageHeader title={<>Brukere</>} intro={countText ?? ""} />
				<Flex direction={Direction.row} gap="20px" alignItems={Justify.center}>
					{anyFilterActivated && (
						<Link to="" onClick={resetFilters}>
							Nullstill filter
						</Link>
					)}
					<FormButton
						style={{ width: "180px" }}
						label={`${showFilters ? "Skjul" : "Åpne"} filtre`}
						icon={<FontAwesomeIcon style={{ marginLeft: "0.5rem" }} icon={faFilterList} />}
						onClick={() => setShowFilters(!showFilters)}
					/>
				</Flex>
			</Flex>
			{showFilters && (
				<UsersFilter
					gridOwners={gridOwners?.gridOwners ?? []}
					gridOwner={gridOwner}
					setGridOwner={setGridOwner}
					ownerType={ownerType}
					setOwnerType={setOwnerType}
					notCustomer={notCustomer}
					setNotCustomer={setNotCustomer}
					fjutt={fjutt}
					setFjutt={setFjutt}
					production={production}
					setProduction={setProduction}
				/>
			)}
			<input
				type="text"
				value={clientSearch}
				onChange={(e) => {
					setClientSearch(e.target.value);
					debouncedSetSearch(e.target.value);
				}}
				placeholder="Søk etter navn, telefonnummer eller e-post"
			/>
			<Flex direction={Direction.column} gap="0px" style={{ borderRadius: "15px", overflow: "hidden" }}>
				{error && !loading && <Error errorMessage="Feil ved henting av brukere" />}
				{loading && <Loading />}
				{!loading && data?.paginatedUsers?.items?.map((user) => <UserRow key={user?.userId} user={user as User} />)}
			</Flex>

			<Pagination
				total={totalPages}
				perPage={PER_PAGE}
				currentPage={clientPage - 1}
				onChange={(newCurrentPage) => {
					const newPage = newCurrentPage + 1;
					setClientPage(newPage);
					setCurrentPage(String(newPage));
					window.scrollTo(0, 0);
				}}
			/>
		</Flex>
	);
}

export const HeaderModalButton = styled(ModalButton)`
	height: 40px;
	width: 40px;
	margin-left: 16px;
	padding: 0;
	line-height: 0;
	border: none;
	border-radius: 50%;
`;
